import { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'

export function BulkPolicyUpdateModal({ selectedIds, onClose, onSuccess, sidebarCollapsed }) {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState('')
  const [entities, setEntities] = useState([])
  const [clients, setClients] = useState([])
  const [advisors, setAdvisors] = useState([])
  const [formData, setFormData] = useState({
    client_id: '',
    category: '',
    product: '',
    entity_id: '',
    start_date: '',
    end_date: '',
  })
  const [selectedAdvisors, setSelectedAdvisors] = useState([])
  const [splits, setSplits] = useState({})
  const [addAdvisorId, setAddAdvisorId] = useState('')

  useEffect(() => {
    supabase.from('entities').select('id, name').order('name')
      .then(({ data }) => setEntities(data || []))
    supabase.from('clients').select('id, full_name').eq('active', true).order('full_name')
      .then(({ data }) => setClients(data || []))
    supabase.from('advisors').select('id, full_name').eq('active', true).order('full_name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addAdvisor = () => {
    if (!addAdvisorId || selectedAdvisors.includes(addAdvisorId)) return
    setSelectedAdvisors(prev => [...prev, addAdvisorId])
    setSplits(prev => ({ ...prev, [addAdvisorId]: '' }))
    setAddAdvisorId('')
  }

  const removeAdvisor = (id) => {
    setSelectedAdvisors(prev => prev.filter(a => a !== id))
    setSplits(prev => { const next = { ...prev }; delete next[id]; return next })
  }

  const updateSplit = (id, val) => {
    setSplits(prev => ({ ...prev, [id]: val }))
  }

  const totalSplit = selectedAdvisors.reduce((sum, id) => sum + (Number(splits[id]) || 0), 0)
  const availableAdvisors = advisors.filter(a => !selectedAdvisors.includes(a.id))

  const handleUpdate = async () => {
    setError('')
    setUpdating(true)

    try {
      const updatePayload = {}
      if (formData.client_id) updatePayload.client_id = formData.client_id
      if (formData.category) updatePayload.category = formData.category
      if (formData.product) updatePayload.product = formData.product
      if (formData.entity_id) updatePayload.entity_id = formData.entity_id
      if (formData.start_date) updatePayload.start_date = formData.start_date
      if (formData.end_date) updatePayload.end_date = formData.end_date

      if (Object.keys(updatePayload).length === 0 && selectedAdvisors.length === 0) {
        setError('Please fill in at least one field to update')
        setUpdating(false)
        return
      }

      // Update policy fields
      if (Object.keys(updatePayload).length > 0) {
        const { error: updateError } = await supabase
          .from('policies')
          .update(updatePayload)
          .in('id', [...selectedIds])

        if (updateError) throw updateError
      }

      // Update advisors (replace existing assignments)
      if (selectedAdvisors.length > 0) {
        // Delete existing advisor assignments
        const { error: deleteError } = await supabase
          .from('policy_advisors')
          .delete()
          .in('policy_id', [...selectedIds])

        if (deleteError) throw deleteError

        // Insert new advisors for all selected policies
        const { error: insertError } = await supabase
          .from('policy_advisors')
          .insert(
            [...selectedIds].flatMap(policyId =>
              selectedAdvisors.map(advisorId => ({
                policy_id: policyId,
                advisor_id: advisorId,
                split_percentage: splits[advisorId] !== '' ? Number(splits[advisorId]) : null,
              }))
            )
          )

        if (insertError) throw insertError
      }

      onSuccess()
    } catch (err) {
      setError(err.message || 'Failed to update policies')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Bulk Update Policies"
      sidebarCollapsed={sidebarCollapsed}
    >
      <div className="space-y-4 max-w-lg">
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-sm text-blue-800">
            <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? 'policy' : 'policies'} will be updated
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Only fill in the fields you want to update. Empty fields will be skipped.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-sfp-darkgray mb-1">Client</label>
          <select
            value={formData.client_id}
            onChange={(e) => handleInputChange('client_id', e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select Client (optional)</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.full_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-sfp-darkgray mb-2">Advisors</label>
          <p className="text-xs text-gray-500 mb-2">Assign multiple advisors with commission split percentages.</p>

          {/* Add advisor row */}
          <div className="flex gap-2 mb-3">
            <select
              value={addAdvisorId}
              onChange={e => setAddAdvisorId(e.target.value)}
              className="input-field flex-1 text-sm"
            >
              <option value="">+ Select advisor to add...</option>
              {availableAdvisors.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
            <Button type="button" variant="secondary" onClick={addAdvisor} disabled={!addAdvisorId}>Add</Button>
          </div>

          {/* Advisor split table */}
          {selectedAdvisors.length > 0 ? (
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-sfp-darkgray">Advisor</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-sfp-darkgray w-28">Split %</th>
                    <th className="w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedAdvisors.map(id => {
                    const advisor = advisors.find(a => a.id === id)
                    return (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-sfp-darkgray">{advisor?.full_name ?? id}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={splits[id] ?? ''}
                              onChange={e => updateSplit(id, e.target.value)}
                              className="input-field w-20 py-1 text-sm"
                              placeholder="0"
                            />
                            <span className="text-gray-400 text-xs">%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" onClick={() => removeAdvisor(id)} className="text-red-400 hover:text-red-600 text-xs font-medium">Remove</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr>
                    <td className="px-3 py-2 text-xs font-semibold text-gray-500">Total</td>
                    <td className="px-3 py-2" colSpan={2}>
                      <span className={`text-sm font-bold ${
                        totalSplit === 100 ? 'text-green-600' :
                        totalSplit > 100 ? 'text-red-600' :
                        totalSplit > 0 ? 'text-amber-500' : 'text-gray-400'
                      }`}>
                        {totalSplit.toFixed(totalSplit % 1 !== 0 ? 2 : 0)}%
                        {totalSplit === 100 && ' ✓'}
                        {totalSplit > 100 && ' — exceeds 100%'}
                        {totalSplit > 0 && totalSplit < 100 && ` — ${(100 - totalSplit).toFixed(2)}% unallocated`}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded">
              No advisors assigned. Use the dropdown above to add advisors.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-sfp-darkgray mb-1">Category</label>
          <input
            type="text"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className="input-field w-full"
            placeholder="e.g., Life Insurance"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-sfp-darkgray mb-1">Product</label>
          <input
            type="text"
            value={formData.product}
            onChange={(e) => handleInputChange('product', e.target.value)}
            className="input-field w-full"
            placeholder="e.g., Term Life"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-sfp-darkgray mb-1">Entity</label>
          <select
            value={formData.entity_id}
            onChange={(e) => handleInputChange('entity_id', e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select Entity (optional)</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>{entity.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-sfp-darkgray mb-1">Start Date</label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => handleInputChange('start_date', e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-sfp-darkgray mb-1">End Date</label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => handleInputChange('end_date', e.target.value)}
              className="input-field w-full"
            />
          </div>
        </div>

        <div className="pt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={updating}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <Button variant="primary" onClick={handleUpdate} disabled={updating}>
            {updating ? 'Updating...' : `Update ${selectedIds.size} Policies`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
